import { type SvgIconComponent } from '@mui/icons-material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MuiAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Icon from '@mui/material/Icon';
import type { ReactNode, RefObject } from 'react';
import { useState } from 'react';

import { interactiveSurfaceSx } from '../Surface/surfaceSx';
import { Text } from '../Text/Text';

export { AccordionSummary, Accordion as MuiAccordion } from '@mui/material';

interface Props {
  title: string;
  compact?: boolean;
  id?: string;
  icon?: SvgIconComponent;
  iconColor?: string;
  children: ReactNode;
  scrollRefOnExpanded?: RefObject<HTMLDivElement | null>;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

// Accordion component controls "expanded" so children are loaded on demand
export function Accordion({
  title,
  compact = false,
  id,
  icon,
  iconColor,
  children,
  scrollRefOnExpanded,
  defaultExpanded = false,
  expanded,
  onExpandedChange,
}: Props) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = expanded ?? internalExpanded;

  return (
    <MuiAccordion
      expanded={isExpanded}
      disableGutters
      elevation={0}
      onChange={(_, nextExpanded) => {
        if (expanded === undefined) {
          setInternalExpanded(nextExpanded);
        }
        onExpandedChange?.(nextExpanded);
        if (scrollRefOnExpanded?.current) {
          scrollRefOnExpanded.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }}
      sx={{
        ...interactiveSurfaceSx,
        alignSelf: 'flex-start',
        width: '100%',
        '::before': { display: 'none' },
      }}
    >
      <AccordionSummary
        aria-controls="panel1a-content"
        expandIcon={<ExpandMore />}
        id={id ?? 'accordion-summary'}
        sx={{
          minHeight: compact ? '2.5rem' : undefined,
          px: '1rem',
          py: compact ? 0 : '0.25rem',
          '&.Mui-expanded': {
            minHeight: compact ? '2.5rem' : undefined,
          },
          '& .MuiAccordionSummary-content': {
            my: compact ? 0 : undefined,
          },
          '& .MuiAccordionSummary-content.Mui-expanded': {
            my: compact ? 0 : undefined,
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          {icon && <Icon component={icon} sx={{ color: iconColor }} />}
          <Text>{title}</Text>
        </Box>
      </AccordionSummary>

      {isExpanded && <AccordionDetails sx={{ px: '1rem' }}>{children}</AccordionDetails>}
    </MuiAccordion>
  );
}
